import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ReleaseGateRow {
  readonly gateId: string;
  readonly gateCode: string;
  readonly releaseRef: string;
  readonly status: string;
  readonly coverageGateStatus: string;
  readonly securityCertStatus: string;
  readonly auditHealthStatus: string;
  readonly rejectionReason: string | null;
}

const STATUSES = ['', 'PENDING', 'READY', 'APPROVED', 'REJECTED', 'BLOCKED'];

/**
 * GR_M31-M35 §F/§Q2/§Q10 — Release Gate console (`/admin/devops/release-gates`).
 *
 * Surfaces the cross-module deployment gate built in the backend (M32 release ↔
 * M34 coverage / M30 security / M21 audit-health). A gate only becomes READY when
 * every evidence dimension has PASSED; a FAILED dimension (e.g. an M34
 * `qa.coverage_gate.failed` event) forces it to BLOCKED and a production deploy is
 * refused (§Q2). Approving/rejecting a gate is a privileged action —
 * `M32_RELEASE_GATE_APPROVE`, held by RELEASE_MANAGER (§Q10). The backend is the
 * sole authority: a caller lacking the permission receives a 403 which this screen
 * surfaces verbatim, rather than the UI silently hiding the control.
 */
export function ReleaseGatesPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ReleaseGateRow[]>([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNote, setActionNote] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: ReleaseGateRow[] }>(`/v1/devops/release-gates${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, reloadKey]);

  const mapActionError = (e: unknown): string => {
    if (e instanceof ApiError && e.status === 403) {
      return t('m32.gates.authError');
    }
    return (e as Error).message;
  };

  const approve = useCallback(async (gateId: string) => {
    setBusyId(gateId);
    setActionError(null);
    setActionNote(null);
    try {
      await apiRequest(`/v1/devops/release-gates/${gateId}/approve`, { method: 'POST', body: {}, token });
      setActionNote(t('m32.gates.approved'));
      refresh();
    } catch (e) {
      setActionError(mapActionError(e));
    } finally {
      setBusyId(null);
    }
  }, [token, refresh]);

  const submitReject = useCallback(async (gateId: string) => {
    if (!reason.trim()) { setActionError(t('m32.gates.reasonRequired')); return; }
    setBusyId(gateId);
    setActionError(null);
    setActionNote(null);
    try {
      await apiRequest(`/v1/devops/release-gates/${gateId}/reject`, { method: 'POST', body: { reason: reason.trim() }, token });
      setActionNote(t('m32.gates.rejected'));
      setRejectingId(null);
      setReason('');
      refresh();
    } catch (e) {
      setActionError(mapActionError(e));
    } finally {
      setBusyId(null);
    }
  }, [token, reason, refresh]);

  return (
    <main aria-label="release-gates">
      <h1>{t('m32.gates.title')}</h1>
      <p className="hint">
        {t('m32.gates.hint')}
      </p>
      <div className="filters">
        <label>
          {t('m32.gates.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m32.gates.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {actionError && <p role="alert" aria-label="action-error">{actionError}</p>}
      {actionNote && <p role="status" aria-label="action-note">{actionNote}</p>}
      {loading ? <p>{t('m32.gates.loading')}</p> : (
        <>
          <p aria-label="gate-count">{t('m32.gates.count', { count: rows.length })}</p>
          <table aria-label="gate-table">
            <thead>
              <tr>
                <th>{t('m32.gates.thGate')}</th><th>{t('m32.gates.thRelease')}</th><th>{t('m32.gates.thCoverage')}</th><th>{t('m32.gates.thSecurity')}</th><th>{t('m32.gates.thAudit')}</th><th>{t('m32.gates.status')}</th><th>{t('m32.gates.thAction')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.gateId} data-status={g.status}>
                  <td>{g.gateCode}</td>
                  <td>{g.releaseRef}</td>
                  <td data-dim="coverage">{g.coverageGateStatus}</td>
                  <td data-dim="security">{g.securityCertStatus}</td>
                  <td data-dim="audit">{g.auditHealthStatus}</td>
                  <td>{g.status}{g.status === 'REJECTED' && g.rejectionReason ? ` — ${g.rejectionReason}` : ''}</td>
                  <td>
                    {g.status === 'READY' ? (
                      rejectingId === g.gateId ? (
                        <span>
                          <label>
                            {t('m32.gates.reason')}
                            <input
                              aria-label={`reject-reason-${g.gateId}`}
                              value={reason}
                              onChange={(e) => setReason(e.target.value)}
                            />
                          </label>
                          <button
                            type="button"
                            aria-label={`confirm-reject-${g.gateId}`}
                            disabled={busyId === g.gateId}
                            onClick={() => void submitReject(g.gateId)}
                          >
                            {t('m32.gates.confirmReject')}
                          </button>
                        </span>
                      ) : (
                        <span>
                          <button
                            type="button"
                            aria-label={`approve-${g.gateId}`}
                            disabled={busyId === g.gateId}
                            onClick={() => void approve(g.gateId)}
                          >
                            {t('m32.gates.approve')}
                          </button>
                          <button
                            type="button"
                            aria-label={`reject-${g.gateId}`}
                            disabled={busyId === g.gateId}
                            onClick={() => { setRejectingId(g.gateId); setReason(''); setActionError(null); }}
                          >
                            {t('m32.gates.reject')}
                          </button>
                        </span>
                      )
                    ) : (
                      <span aria-label={`no-action-${g.gateId}`}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
